// Central definitions for status enums.
//
// Each enum is declared once here as:
//   - a `*_STATUSES` tuple   → feeds `z.enum(...)` in forms and the DB schema's type
//   - a `*_STATUS_OPTIONS`    → feeds `EnumSelect` (value + human label)
//
// Keeping both in one place means a new status is added in exactly one spot and
// every form, filter, and create dialog picks it up.

export interface EnumOption {
  value: string;
  label: string;
}

// ── Widget (the reference entity — see src/db/schema/widget.ts) ──
//
// `widget` is the template's one example entity. When you add a real entity,
// declare its statuses here the same way and import them in its form/filter.
export const WIDGET_STATUSES = ["draft", "active", "archived"] as const;
export type WidgetStatus = (typeof WIDGET_STATUSES)[number];
export const WIDGET_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

// ── Fleet Relay: copy operations ──
//
// A copy operation always targets exactly one object type (never combined).
// Values are lowercase snake_case for consistency with every other enum in
// this file — the Coach Manager SOAP client (src/lib/coach-manager/) maps
// these to the vendor's PascalCase operation-name segments itself.
export const COPY_OBJECT_TYPES = ["contract", "private_hire", "quotation"] as const;
export type CopyObjectType = (typeof COPY_OBJECT_TYPES)[number];
export const COPY_OBJECT_TYPE_OPTIONS: readonly EnumOption[] = [
  { value: "contract", label: "Contract" },
  { value: "private_hire", label: "Private Hire" },
  { value: "quotation", label: "Quotation" },
];

// `copy_operation.status`. `rolled_back` is retained for forward compatibility
// only — the current single-consolidated-Insert-call design means a copy
// either succeeds whole or fails whole, so it's expected to rarely if ever
// be exercised (see .claude/rules — Fleet Relay build plan §4.2).
export const COPY_OPERATION_STATUSES = [
  "pending",
  "in_progress",
  "completed",
  "failed",
  "rolled_back",
] as const;
export type CopyOperationStatus = (typeof COPY_OPERATION_STATUSES)[number];
export const COPY_OPERATION_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "rolled_back", label: "Rolled Back" },
];

// `copy_operation_entity.status` — one row per entity created (or attempted)
// in the target system as part of a copy operation.
export const COPY_OPERATION_ENTITY_STATUSES = ["created", "failed", "rolled_back"] as const;
export type CopyOperationEntityStatus = (typeof COPY_OPERATION_ENTITY_STATUSES)[number];
export const COPY_OPERATION_ENTITY_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "created", label: "Created" },
  { value: "failed", label: "Failed" },
  { value: "rolled_back", label: "Rolled Back" },
];

// `master_data_mapping.entityType` — what kind of Coach Manager master-data
// reference a mapping row resolves. Starts with the set needed for the
// Contract build order; grows additively (e.g. `private_hire_data_title`,
// `quotation_data_title`, `vehicle`) as PrivateHire and Quotation support is
// added — same one-line-per-addition pattern as AUDIT_ACTIONS below.
//
// Note: `nominal_code`/`vat_code` are listed here for that future growth,
// not because Contract needs them today — confirmed against the live WSDL
// (Phase 1), `NominalCode`/`VATCode` only exist on `PrivateHireVehicle`/
// `PrivateHireExtra`/`QuotationVehicle`/`QuotationExtra`, not on
// `ContractVehicle`. Harmless to keep (Phase 3/6 will need them), just not
// exercised until PrivateHire/Quotation support lands.
export const MASTER_DATA_ENTITY_TYPES = [
  "operator",
  "booking_type",
  "base",
  "vehicle_type",
  "driver_type",
  "nominal_code",
  "vat_code",
  "facility",
  "included_item",
  "contract_data_title",
  "contract_movement_data_title",
] as const;
export type MasterDataEntityType = (typeof MASTER_DATA_ENTITY_TYPES)[number];

/** Runtime guard for the `/$entityType` route param — see `router.tsx`. */
export function isMasterDataEntityType(value: string): value is MasterDataEntityType {
  return (MASTER_DATA_ENTITY_TYPES as readonly string[]).includes(value);
}

export const MASTER_DATA_ENTITY_TYPE_OPTIONS: readonly EnumOption[] = [
  { value: "operator", label: "Operator" },
  { value: "booking_type", label: "Booking Type" },
  { value: "base", label: "Base / Depot" },
  { value: "vehicle_type", label: "Vehicle Type" },
  { value: "driver_type", label: "Driver Type" },
  { value: "nominal_code", label: "Nominal Code" },
  { value: "vat_code", label: "VAT Code" },
  { value: "facility", label: "Facility" },
  { value: "included_item", label: "Included Item" },
  { value: "contract_data_title", label: "Contract Data Title" },
  { value: "contract_movement_data_title", label: "Contract Movement Data Title" },
];

// `ContractSearch`'s `DateCreatedOperator`/`LastUpdatedOperator` — a passthrough
// value to the Coach Manager SOAP API, so this stays in the wire's own
// PascalCase (matching `SearchOperator` in src/lib/coach-manager/types.ts)
// rather than the snake_case every DB-persisted enum in this file uses.
export const SEARCH_OPERATOR_OPTIONS: readonly EnumOption[] = [
  { value: "GreaterThan", label: "After" },
  { value: "EqualTo", label: "On" },
  { value: "LessThan", label: "Before" },
];

// ── Fleet Relay: SOAP API call log ──
//
// `soap_api_call_log.operation` — the Coach Manager SOAP operation the row
// records. Kept in the vendor's own PascalCase (not snake_case) since these
// are wire operation names, same posture as SEARCH_OPERATOR_OPTIONS above.
export const SOAP_OPERATIONS = [
  "Login",
  "ContractSearch",
  "InsertContract",
  "ClientSearch",
  "PrivateHireSearch",
] as const;
export type SoapOperation = (typeof SOAP_OPERATIONS)[number];
export const SOAP_OPERATION_OPTIONS: readonly EnumOption[] = SOAP_OPERATIONS.map((value) => ({
  value,
  label: value,
}));

// `soap_api_call_log.status` — the outcome of one SOAP call: transport
// failure, SOAP fault, non-2xx, unexpected shape, or a business
// `Result !== "Success"` all collapse to "error"; only a fully successful
// call is "success".
export const SOAP_CALL_STATUSES = ["success", "error"] as const;
export type SoapCallStatus = (typeof SOAP_CALL_STATUSES)[number];
export const SOAP_CALL_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "success", label: "Success" },
  { value: "error", label: "Error" },
];

// ── Audit log ──
//
// `action` is the `<entity>.<verb>` string stored on every audit_log row, and
// `entityType` is the coarse kind it groups under. Both are validated against
// these allowlists in `recordAudit` (src/lib/audit.ts) and drive the filter
// dropdowns in the admin viewer. Add a new action here when you audit a new
// mutation — the helper rejects anything not listed (see .claude/rules/audit.md).

export const AUDIT_ENTITY_TYPES = [
  "widget",
  "user",
  "auth",
  "bu_instance",
  "master_data_mapping",
  "copy_operation",
  "soap_api_log_settings",
  "bu_instance_label_settings",
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

export const AUDIT_ACTIONS = [
  // Reference-entity CRUD (src/examples/widget). Mirror this block per entity
  // you audit: `<entity>.created` / `.updated` / `.deleted`.
  "widget.created",
  "widget.updated",
  "widget.deleted",
  // Authentication events (captured via Better Auth hooks).
  "auth.login",
  "auth.login_failed",
  "auth.logout",
  "auth.signup",
  "auth.password_reset_requested",
  "auth.password_reset_completed",
  // Admin user management (Better Auth admin plugin endpoints).
  "user.created",
  "user.deleted",
  "user.role_changed",
  "user.banned",
  "user.unbanned",
  "user.password_reset",
  // Fleet Relay: BU instance connection config (admin-only — see .claude/rules
  // Fleet Relay build plan §5).
  "bu_instance.created",
  "bu_instance.updated",
  "bu_instance.deleted",
  // Fleet Relay: master-data mapping CRUD.
  "master_data_mapping.created",
  "master_data_mapping.updated",
  "master_data_mapping.deleted",
  // Fleet Relay: copy operation lifecycle. `.rolled_back` is forward-compat,
  // mirroring COPY_OPERATION_STATUSES above.
  "copy_operation.initiated",
  "copy_operation.completed",
  "copy_operation.failed",
  "copy_operation.rolled_back",
  "copy_operation.deleted",
  // Fleet Relay: SOAP API call log capture/retention config + manual purge.
  "soap_api_log_settings.updated",
  "soap_api_log.purged",
  // Fleet Relay: admin-configurable display term for "BU Instance" (Settings → System).
  "bu_instance_label_settings.updated",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const AUDIT_ACTION_OPTIONS: readonly EnumOption[] = AUDIT_ACTIONS.map((value) => ({
  value,
  label: value,
}));
